import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BtlPreviewRequest,
  BtlPreviewResponse,
  fetchBtlPreview,
} from 'api/video';

/** How long the words and the logo must sit still before the preview is asked for again. */
export const BTL_PREVIEW_DEBOUNCE_MS = 400;

export type BtlPreviewState =
  | { status: 'loading' }
  | {
      status: 'ready';
      answer: BtlPreviewResponse;
      /**
       * A later ask failed: the answer shown is for an earlier wish, not the
       * newest. The screen says so (Steven, 27 Sep 2026: Track 1's note).
       */
      stale?: boolean;
    }
  | { status: 'failed' };

/**
 * SYM-6353. The Bring to Life preview: the words the image ad shows, and the
 * three looks drawn on the ad's picture with the customer's words and logo.
 *
 * Asked for AT ONCE when the pop-up opens, and again every time the words or
 * the logo change -- but only once they have been still for 400 ms, so typing
 * a word is one request and not one per letter.
 *
 * NEVER TWO AT ONCE, AND THE LATEST WINS. The server draws the text for every
 * request, which takes a moment. A change made while a request is on its way
 * does not start a second one next to it: it waits, and when the first comes
 * back, only the newest wish is sent (the ones in between are never asked
 * for). Because one request follows the other, the last answer to arrive is
 * always the answer to the newest wish -- an older answer cannot land on top
 * of a newer one. Same rule as useStyleExamples (review gate, 23 Sep 2026).
 *
 * A failed re-ask leaves the answer that is already on screen, marked
 * `stale`, so the screen can say it is not the latest change and offer to
 * ask again. The next answer that arrives clears the mark.
 *
 * `request` null means "the pop-up is not on its input screen": nothing is
 * asked, and the next open starts fresh.
 */
export const useBtlPreview = (
  request: BtlPreviewRequest | null
): { state: BtlPreviewState; retry: () => void; asking: boolean } => {
  const [state, setState] = useState<BtlPreviewState>({ status: 'loading' });
  /** Whether a request is on its way, for a "Building…" on the retry. */
  const [asking, setAsking] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const key = request ? JSON.stringify(request) : null;

  /** The newest wish, as sent, and its key. */
  const latest = useRef<{ key: string; request: BtlPreviewRequest } | null>(
    null
  );
  /** The key of the request on its way, if any. */
  const inFlight = useRef<string | null>(null);
  /**
   * The request on its way, to be dropped by Try again. Each request has its
   * own number, so an aborted one that settles later changes nothing.
   */
  const outgoing = useRef<{ id: number; abort: AbortController } | null>(null);
  const requestCount = useRef(0);
  /** The key of the last request sent, so a settled wish is not sent twice. */
  const lastSent = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** False until this open has asked once: the first ask is not debounced. */
  const askedThisOpen = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    // Set here as well as above: React runs this twice in development, and
    // the first cleanup would otherwise leave the hook deaf for good.
    mounted.current = true;
    return () => {
      mounted.current = false;
      outgoing.current?.abort.abort();
      outgoing.current = null;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, []);

  const send = useCallback(() => {
    const wish = latest.current;
    if (!wish || inFlight.current) return;

    inFlight.current = wish.key;
    lastSent.current = wish.key;
    setAsking(true);

    requestCount.current += 1;
    const id = requestCount.current;
    const abort = new AbortController();
    outgoing.current = { id, abort };
    const current = () => outgoing.current?.id === id;

    fetchBtlPreview(wish.request, { signal: abort.signal })
      .then((answer) => {
        if (!current() || !mounted.current || latest.current === null) return;
        setState({ status: 'ready', answer });
      })
      .catch(() => {
        if (!current() || !mounted.current || latest.current === null) return;
        setState((shown) =>
          shown.status === 'ready'
            ? { ...shown, stale: true }
            : { status: 'failed' }
        );
      })
      .finally(() => {
        // Dropped by Try again: a fresh request has taken its place.
        if (!current()) return;
        outgoing.current = null;
        inFlight.current = null;
        if (mounted.current) setAsking(false);
        // A newer wish arrived while this one was out: send it now.
        if (
          mounted.current &&
          latest.current &&
          latest.current.key !== lastSent.current &&
          !timer.current
        ) {
          send();
        }
      });
  }, []);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    if (!request || !key) {
      latest.current = null;
      lastSent.current = null;
      askedThisOpen.current = false;
      setState({ status: 'loading' });
      return;
    }

    latest.current = { key, request };

    if (!askedThisOpen.current) {
      askedThisOpen.current = true;
      send();
      return;
    }

    timer.current = setTimeout(() => {
      timer.current = null;
      if (latest.current && latest.current.key !== lastSent.current) send();
    }, BTL_PREVIEW_DEBOUNCE_MS);
    // `request` is read through `key`, which is its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, send]);

  // Try again: drop whatever request is still out and ask afresh, at once.
  // Waiting for the one still out is what kept "Building…" on screen for a
  // minute on QA (27 Sep 2026): it had hung, and Try again queued behind it.
  useEffect(() => {
    if (attempt === 0) return;
    if (!latest.current) return;
    setState((shown) =>
      shown.status === 'failed' ? { status: 'loading' } : shown
    );
    if (outgoing.current) {
      outgoing.current.abort.abort();
      outgoing.current = null;
      inFlight.current = null;
    }
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    lastSent.current = null;
    send();
  }, [attempt, send]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { state, retry, asking };
};

export default useBtlPreview;
